import * as React from "react";
import { HeadingLarge, HeadingSmall } from "kanopi-components/typography";
import { PortalRow, RowGap } from "kanopi-components/PortalRow";
import { Redirect, useNavigate } from "@reach/router";
import {
  useTrackCheckoutStarted,
  useTrackQuoteDetailsUpdated,
  useTrackQuoteViewed,
  useTrackReferralRequired,
} from "../hooks/useTracking";
import { ChevronUp, ChevronDown } from "baseui/icon";

import { Block } from "baseui/block";
import { Button } from "kanopi-components/Button";
import { Callout } from "kanopi-components/Callout";
import { Currency } from "kanopi-components/Currency";
import { FixedFooter } from "kanopi-components/Sticky/FixedFooter";
import { Kontainer } from "kanopi-components/Layout/Kontainer";
import { Placeholder } from "kanopi-components/Placeholder";
import { Products } from "./Products";
import { ProductsLegacy } from "./ProductsLegacy";
import { QuoteDocumentPollList } from "kw/util/DocumentPollList/DocumentPollList";
import { QuoteMessage } from "./QuoteMessage";
import { QuotePrice } from "./QuotePrice";
import { FeeTypeKind, QuoteState } from "kw/api/zeus";
import { QuoteSummarySection } from "kw/quoting/components/QuoteSummarySection";
import { RiskState } from "kw/util/RiskState";
import { SectionHeader } from "kw/components/SectionHeader/SectionHeader";
import { Sektion } from "kanopi-components/Layout/Sektion";
import { SidePadder } from "kanopi-components/Layout/SidePadder";
import { WidthContainer } from "kw/util/ChatFlow";
import { combineQuotesAndCovers } from "../utils/utils";
import { jsonStringToCamelCase } from "kanopi-components/utils/json";
import { parseAndPurifyHtml } from "kw/util/utils";
import { useGlobal } from "kanopi-components/useGlobal";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { useToggleEnabledProducts } from "../hooks/useToggleEnabledProducts";
import { preventDefault } from "kanopi-components/utils/browser";
import { quoteFields, riskProfileFields } from "kw/api/fragments";
import { useAPI } from "kw/api/useAPI";
import { Accordion, Panel } from "kanopi-components/Accordion";
import { FeeSummary } from "kw/policies/components/FeeSummary";

/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/ban-types */
// TODO: Enable the proptype check next time when this file is changed

type TProps = {
  quotes?: TQuote[];
  riskProfile?: TRiskProfile;
  coverSections?: TCoverSection[];
  loading?: boolean;
  priceLoading?: boolean;
  pricingFailed?: boolean;
  priceRiskProfile?: () => void;
};

const StatelessQuote: React.FC<TProps> = (props) => {
  const {
    riskProfile,
    quotes = [],
    coverSections = [],
    loading,
    priceLoading,
    pricingFailed,
    priceRiskProfile,
  } = props;
  const combinedQuote = React.useMemo(
    () => combineQuotesAndCovers(riskProfile, quotes, coverSections),
    [riskProfile, quotes, coverSections],
  );
  const { disabledCoverSections } = combinedQuote;
  const messages = riskProfile?.quotes?.[0]?.messages || null;

  const [, theme] = useStyletron();
  const trackReferralRequired = useTrackReferralRequired({ referralType: "broker" });
  const trackQuoteViewed = useTrackQuoteViewed();
  const [, setQuotingData] = useGlobal("quoting");
  const navigate = useNavigate();
  const trackQuoteDetailsUpdated = useTrackQuoteDetailsUpdated();

  const { toggleEnabledProducts, loading: togglingEnabledProducts } =
    useToggleEnabledProducts(riskProfile?.id, priceRiskProfile);

  const noPrice =
    riskProfile?.periodicPrice === null && riskProfile?.state === QuoteState.QUOTED;
  const isNotQuoted =
    riskProfile?.state === QuoteState.KNOCKOUT
    || riskProfile?.state === QuoteState.REFERRED;
  // Redirect to referral page if the quote is not quoted or no price is returned
  const isKnockedOut = priceLoading === false && (isNotQuoted || noPrice);
  const enabledProductKeys = riskProfile?.enabledProducts?.map((p) => p.key) || [];
  const showProductToggle =
    quotes?.filter((quote) => quote?.state !== QuoteState.KNOCKOUT)?.length > 1;

  React.useEffect(() => {
    if (quotes.length && isKnockedOut) {
      const referralReason = isNotQuoted
        ? "Knocked out"
        : noPrice
          ? "No price returned"
          : "Unknown - investigate";
      trackReferralRequired({ referralReason });
    }
  }, [isKnockedOut]);

  React.useEffect(() => {
    if (!(priceLoading || loading || isKnockedOut)) {
      trackQuoteViewed({
        quote: combinedQuote,
        riskProfile,
        enabledCoverSections: riskProfile?.enabledCovers || [],
      });
    }
  }, [loading, priceLoading, isKnockedOut]);
  const handleUpdate = () => {
    setQuotingData({ restart: true });
    navigate(`/quoting/${riskProfile?.id}`);
    trackQuoteDetailsUpdated({ riskProfile });
  };

  const cannotSkipPayments =
    !riskProfile?.periodicPrice && !riskProfile?.quotes?.[0]?.product?.skipPayments;
  return (
    <Placeholder loading={loading}>
      {isKnockedOut ? (
        <Redirect to={`/quote/referral/${riskProfile?.id}`} noThrow />
      ) : (
        <Kontainer>
          <PortalRow bottomSpace={RowGap.Shortened} paddingBottom={0}>
            <SectionHeader title={combinedQuote?.description} />
            {/*
              TODO: Use quote state to determine the title.
              We should not rely on the messages to determine the title.
              We might need to introduce a new state for declined quotes.
              https://linear.app/kanopicover/issue/K-157/match-bz-quote-status-to-kanopi-quote-status
            */}
            {messages ? (
              <HeadingLarge
                marginTop={theme.sizing.scale600}
                marginBottom={0}
                center
                paddingBottom={0}
              >
                Your quote has been declined
              </HeadingLarge>
            ) : (
              <HeadingLarge
                marginTop={theme.sizing.scale600}
                marginBottom={0}
                center
                paddingBottom={0}
              >
                Here&rsquo;s your quote
              </HeadingLarge>
            )}
          </PortalRow>
          {/*
            TODO: Use quote state to determine if the messages should be displayed.
            https://linear.app/kanopicover/issue/K-157/match-bz-quote-status-to-kanopi-quote-status
          */}
          {messages && (
            <>
              <PortalRow bottomSpace={RowGap.Shortened} paddingBottom={0}>
                <Block data-testid="quote-message">
                  <QuoteMessage messages={JSON.parse(messages)} />
                </Block>
              </PortalRow>
              <Block
                display="flex"
                flexDirection="column"
                alignItems="center"
                marginTop="45px"
                marginBottom="45px"
              >
                <Button
                  kind={Button.KIND.primary}
                  size={Button.SIZE.mini}
                  shape={Button.SHAPE.pill}
                  onClick={handleUpdate}
                  data-testid="riskStateReQuoteButton"
                >
                  Start a new quote
                </Button>
              </Block>
            </>
          )}
          <PortalRow>
            <Block data-testid="quote-summary">
              {!messages && (
                <QuotePrice
                  periodicPrice={riskProfile?.periodicPrice}
                  paymentFrequency={riskProfile?.paymentFrequency}
                  loading={priceLoading || togglingEnabledProducts}
                  pricingFailed={pricingFailed}
                  priceRiskProfile={priceRiskProfile}
                  pricingOthers={riskProfile?.quotes?.[0]?.pricingOthers}
                />
              )}

              <QuoteSummarySection
                riskProfileId={riskProfile?.id}
                priceLoading={priceLoading}
                priceRiskProfile={priceRiskProfile}
              />
            </Block>
          </PortalRow>
          {quotes[0]?.product?.quoteDisclaimer && (
            <PortalRow bottomSpace={RowGap.Shortened}>
              <WidthContainer>
                <Callout highlight={true} hideIcon={true}>
                  <Block>
                    {parseAndPurifyHtml(quotes[0]?.product?.quoteDisclaimer)}
                  </Block>
                </Callout>
              </WidthContainer>
            </PortalRow>
          )}
          <PortalRow>
            {riskProfile?.enabledExposures?.length ? (
              <>
                <Products
                  riskProfile={riskProfile}
                  quotes={quotes}
                  priceLoading={priceLoading}
                  disabledCoverSections={disabledCoverSections}
                  priceRiskProfile={priceRiskProfile}
                />
                <HeadingSmall marginBottom={0} center>
                  Important documents
                </HeadingSmall>
                <QuoteDocumentPollList
                  label=""
                  riskProfileId={riskProfile?.id}
                  shouldPollDocuments={false}
                  quoteId={quotes?.[0]?.id}
                />
              </>
            ) : (
              <ProductsLegacy
                riskProfile={riskProfile}
                quotes={quotes}
                priceLoading={priceLoading}
                pricingFailed={pricingFailed}
                priceRiskProfile={priceRiskProfile}
                disabledCoverSections={disabledCoverSections}
                showProductToggle={showProductToggle}
                enabledProductKeys={enabledProductKeys}
                togglingEnabledProducts={togglingEnabledProducts}
                toggleEnabledProducts={toggleEnabledProducts}
              />
            )}
          </PortalRow>
          <Sektion compact>
            <SidePadder>
              <HeadingSmall marginTop={0} center>
                The details you provided
              </HeadingSmall>
              <RiskState riskProfileId={riskProfile?.id} update={handleUpdate} />
            </SidePadder>
          </Sektion>

          <Block display="flex" justifyContent="center">
            {/* last disabled condition allows 0 price when skipPayments is true */}
            <ResponsiveBuyButton
              riskProfile={riskProfile}
              disabled={
                priceLoading || riskProfile?.periodicPrice == null || cannotSkipPayments
              }
              quote={combinedQuote}
            />
          </Block>
        </Kontainer>
      )}
    </Placeholder>
  );
};

function ResponsiveBuyButton({ riskProfile, disabled, quote }) {
  const trackCheckoutStarted = useTrackCheckoutStarted();
  const navigate = useNavigate();
  const { query } = useAPI();
  const handleClick = () => {
    query(
      {
        riskProfilePaymentAllowed: [
          {
            id: riskProfile?.id,
          },
          true,
        ],
      },
      () => {
        navigate(`/quoting/knockout/${riskProfile?.id}/`);
        trackCheckoutStarted({ riskProfile, quote });
      },
    );
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, theme] = useStyletron();
  return (
    <FixedFooter>
      <Block
        padding={["scale600", "scale600", "scale1600"]}
        display="flex"
        justifyContent="center"
      >
        <Button
          kind="primary"
          wide
          onClick={handleClick}
          disabled={disabled}
          data-testid="buyButton"
          // TODO: Not this. Copied from NextButton.
          overrides={{
            BaseButton: {
              style: ({ $theme }) => ({
                [`@media screen and (max-width: ${$theme.breakpoints.medium}px)`]: {
                  width: "100%",
                },
              }),
            },
          }}
        >
          {!riskProfile?.quotes?.[0]?.product?.skipPayments ? (
            <>
              Buy for&nbsp;
              <Currency value={riskProfile?.periodicPrice} />
            </>
          ) : (
            "Confirm"
          )}
        </Button>
      </Block>
    </FixedFooter>
  );
}

export { StatelessQuote };
